// RITA-1522 (Nutzungserfassung Phase 3d, Epic RITA-1496) — Schalter-Block „Heatmap".
//
// Liegt bei seinen Geschwistern im Einstellungs-Drawer (NACH „Farbvorlagen", durch einen Divider
// abgesetzt), gehört aber NICHT zu den Einstellungen: der Zustand steht im HeatmapProvider und
// wird nirgends gespeichert.
//
// Folge daraus — und der Grund für diesen Kommentar: der Schalter ist vom „Zurücksetzen" des
// Drawers AUSGENOMMEN. `notDefault` in SettingsDrawer.tsx vergleicht nur die gespeicherten
// Einstellungen mit ihren Vorgaben; die Heatmap kommt dort bewusst nicht vor. Ein Zurücksetzen
// der Darstellung darf die laufende Auswertung nicht abschalten, und der Punkt „geändert" am
// Zahnrad soll nicht angehen, weil jemand die Heatmap eingeschaltet hat.
//
// Sichtbar NUR für SUPERADMIN (gemeinsamer Helfer utils/istSuperAdmin.ts, Rolle aus dem Token wie
// im Provider und in Routes.tsx). Das Gate ist
// kosmetisch — verbindlich sind usageAccess.ts und die Whitelist im Backend —, aber ein Schalter,
// der jedem anderen nur ein Forbidden einbrächte, gehört nicht in die Oberfläche.
import React, { useContext } from 'react';
import { Divider, Stack, Switch, Typography } from '@mui/material';
import UserContext from '../../../context/UserProvider/UserContext';
import { getCurrentUser } from '../../../context/CommunicationProvider/authUser';
import { istSuperAdmin } from '../../../utils/istSuperAdmin';
import { MARKERS } from '../../../features/usage-tracking/usageKeys';
import { useHeatmap } from '../../../features/usage-tracking/heatmap/HeatmapContext';
import Block from './Block';

export default function HeatmapOptions() {
  const { enabled, setEnabled } = useHeatmap();
  const user = useContext(UserContext);

  // Dieselbe Prüfung wie im HeatmapProvider, aus denselben zwei Gründen: der TOKEN, weil der
  // Server mit genau ihm autorisiert und ein Rollenwechsel in der laufenden Anwendung sofort
  // wirken muss; der NUTZERKONTEXT, weil nur er sich ändert, wenn das Profil die Rolle entzieht.
  // Ein Schalter, der stehen bliebe, nachdem das Overlay sich selbst abgeschaltet hat, wäre
  // schlimmer als keiner.
  if (!istSuperAdmin(getCurrentUser()) || !istSuperAdmin(user)) return null;

  // Der Trenner gehört zum Block, nicht zum Drawer (KI-Review !746): er setzt die Heatmap
  // sichtbar von den Darstellungsblöcken ab — und eine Linie ohne Block dahinter wäre für
  // jeden Nicht-SUPERADMIN ein sichtbarer Rest. Abstand wie der Drawer (SPACING 2.5).
  return (
    <>
      <Divider sx={{ borderStyle: 'dashed', mb: 2.5 }} />
      <Block title="Heatmap">
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Typography variant="body2">Klick-Heatmap anzeigen</Typography>
          <Switch
            size="small"
            checked={enabled}
            onChange={(event) => setEnabled(event.target.checked)}
            // Marker am EINGABEFELD, nicht am Rahmen: resolveClickTarget nimmt den nächsten
            // markierten Vorfahren, und das wäre beim Rahmen ein <span> mit Elementart 'other'.
            // Die Typzusicherung ist der Weg des Bestands für data-Attribute in inputProps
            // (McpAdminPage.tsx).
            inputProps={
              {
                'aria-label': 'Klick-Heatmap anzeigen',
                // Der Klick wird wie an jedem markierten Bedienelement als normaler click erfasst;
                // ein eigenes Ereignis gibt es nicht, und ein setting-Ereignis wäre falsch, weil
                // hier keine Einstellung geändert wird.
                'data-usage': MARKERS.einstellungenHeatmap,
              } as React.InputHTMLAttributes<HTMLInputElement>
            }
          />
        </Stack>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Nur für die Auswertung, wird nicht gespeichert. Zeigt die Klicks der gewählten Tage auf dieser Seite als Wolke
          und als Zähler an den Bedienelementen. Blockiert keine Bedienung und geht beim Abmelden aus.
        </Typography>
      </Block>
    </>
  );
}
